import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Users, TrendingUp, UserCheck, MessageSquare, Target, Heart,
  BarChart3, Activity, Send, Clock, CheckCircle, XCircle, Mail, Trash2, UserPlus, Globe, CircleDot, Wrench
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, AreaChart
} from "recharts";

const API = '/api';

const StatCard = ({ title, value, subtitle, icon: Icon, color = "violet", testId }) => {
  const colors = {
    violet: "bg-violet-50 text-violet-600",
    green: "bg-emerald-50 text-emerald-600",
    blue: "bg-blue-50 text-blue-600",
    amber: "bg-amber-50 text-amber-600",
    rose: "bg-rose-50 text-rose-600",
    slate: "bg-slate-50 text-slate-600"
  };

  return (
    <Card data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500 mb-1">{title}</p>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-2.5 rounded-lg ${colors[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const CHART_COLORS = ["#7C3AED", "#10B981", "#F59E0B", "#EF4444", "#3B82F6", "#8B5CF6", "#EC4899", "#06B6D4"];

const AdminDashboard = () => {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [growth, setGrowth] = useState([]);
  const [activity, setActivity] = useState([]);
  const [invites, setInvites] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { user_id, name, email }
  const [growthGranularity, setGrowthGranularity] = useState("daily");
  const [activityGranularity, setActivityGranularity] = useState("daily");

  // Maintenance: pending-invite backfill state
  const [backfillState, setBackfillState] = useState({ running: false, lastResult: null, confirming: false });

  const runBackfill = async (dryRun) => {
    setBackfillState((s) => ({ ...s, running: true, confirming: false }));
    try {
      const res = await fetch(
        `${API}/admin/network/backfill-pending-invites?dry_run=${dryRun ? "true" : "false"}`,
        { method: "POST", credentials: "include" },
      );
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${txt ? ` – ${txt.slice(0, 120)}` : ""}`);
      }
      const data = await res.json();
      setBackfillState({ running: false, lastResult: data, confirming: false });
      if (dryRun) {
        toast.success(`Dry run · would claim ${data.claimed} of ${data.examined} pending invites`);
      } else {
        toast.success(`Claimed ${data.claimed} pending invite${data.claimed === 1 ? "" : "s"}`);
      }
    } catch (e) {
      setBackfillState((s) => ({ ...s, running: false }));
      toast.error(e.message || "Backfill failed");
    }
  };

  const fetchAll = useCallback(async () => {
    try {
      const [ovRes, grRes, acRes, inRes, enRes] = await Promise.all([
        fetch(`${API}/admin/metrics/overview`, { credentials: "include" }),
        fetch(`${API}/admin/metrics/growth?granularity=${growthGranularity}`, { credentials: "include" }),
        fetch(`${API}/admin/metrics/activity?granularity=${activityGranularity}`, { credentials: "include" }),
        fetch(`${API}/admin/metrics/invites`, { credentials: "include" }),
        fetch(`${API}/admin/metrics/engagement`, { credentials: "include" }),
      ]);
      if (ovRes.ok) setOverview(await ovRes.json());
      if (grRes.ok) setGrowth(await grRes.json());
      if (acRes.ok) setActivity(await acRes.json());
      if (inRes.ok) setInvites(await inRes.json());
      if (enRes.ok) setEngagement(await enRes.json());
    } catch (e) {
      console.error("[ADMIN] metrics fetch error:", e);
    }

    // Fetch users separately to avoid one failure blocking everything
    try {
      const usRes = await fetch(`${API}/admin/users`, { credentials: "include" });
      if (usRes.ok) {
        const usData = await usRes.json();
        console.log("[ADMIN] users response:", usRes.status, "count:", Array.isArray(usData) ? usData.length : "NOT_ARRAY");
        setUsersList(Array.isArray(usData) ? usData : []);
      } else {
        const errText = await usRes.text().catch(() => "");
        console.error("[ADMIN] users FAILED:", usRes.status, errText);
      }
    } catch (e) {
      console.error("[ADMIN] users fetch error:", e);
    }

    try {
      const wlRes = await fetch(`${API}/admin/waitlist`, { credentials: "include" });
      if (wlRes.ok) setWaitlist(await wlRes.json());
    } catch (e) {
      console.error("[ADMIN] waitlist fetch error:", e);
    }

    setLoading(false);
  }, [growthGranularity, activityGranularity]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const deleteUser = async (userId) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (res.ok) {
        setUsersList(prev => prev.filter(u => u.user_id !== userId));
        setDeleteConfirm(null);
        // Refresh overview stats
        const ovRes = await fetch(`${API}/admin/metrics/overview`, { credentials: "include" });
        if (ovRes.ok) setOverview(await ovRes.json());
      }
    } catch (e) {
    }
  };

  if (!user || user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const inviteStatusData = invites?.by_status
    ? Object.entries(invites.by_status).map(([name, value]) => ({ name, value }))
    : [];

  const roleData = invites?.by_role
    ? Object.entries(invites.by_role).map(([name, value]) => ({ name: name.replace("_", " "), value }))
    : [];

  const toolData = engagement?.by_tool
    ? Object.entries(engagement.by_tool).map(([name, value]) => ({ name: name.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase()), value }))
    : [];

  const formatDate = (d) => {
    if (!d) return "";
    const parts = d.split("-");
    if (parts.length === 3) return `${parts[1]}/${parts[2]}`;
    return d;
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="admin-dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1
          className="text-2xl md:text-3xl font-bold text-slate-900"
          style={{ fontFamily: "system-ui, sans-serif" }}
          data-testid="admin-title"
        >
          Admin Dashboard
        </h1>
        <p className="text-slate-500 mt-1">Platform metrics and user analytics</p>
      </div>

      {/* KPI Cards */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8" data-testid="kpi-cards">
          <StatCard title="Total Users" value={overview.total_users} icon={Users} color="violet" testId="stat-total-users" />
          <StatCard title="Organic" value={overview.organic_users} icon={Globe} color="green" testId="stat-organic" />
          <StatCard title="Invited" value={overview.invited_users} icon={UserPlus} color="blue" testId="stat-invited" />
          <StatCard title="Active (7d)" value={overview.active_7d} icon={Activity} color="green" testId="stat-active-7d" />
          <StatCard title="Active (30d)" value={overview.active_30d} icon={TrendingUp} color="blue" testId="stat-active-30d" />
          <StatCard title="Connections" value={overview.accepted_connections} subtitle={`${overview.pending_connections} pending`} icon={UserCheck} color="amber" testId="stat-connections" />
          <StatCard title="Insights" value={overview.total_insight_requests} subtitle={`${overview.insight_completion_rate}% completed`} icon={MessageSquare} color="rose" testId="stat-insights" />
          <StatCard title="Conversations" value={overview.total_messages} icon={Send} color="slate" testId="stat-messages" />
        </div>
      )}

      {/* Row 1: Growth + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* User Growth */}
        <Card data-testid="chart-growth">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-violet-500" />
                User Growth
              </CardTitle>
              <div className="flex bg-slate-100 rounded-lg p-0.5" data-testid="growth-granularity">
                {["daily", "weekly", "monthly"].map(g => (
                  <button
                    key={g}
                    onClick={() => setGrowthGranularity(g)}
                    className={`text-[10px] font-medium px-2.5 py-1 rounded-md transition-colors capitalize ${
                      growthGranularity === g ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                    data-testid={`growth-${g}`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {growth.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={growth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip labelFormatter={(v) => `Date: ${v}`} />
                  <Area type="monotone" dataKey="total" stroke="#7C3AED" fill="#7C3AED" fillOpacity={0.1} strokeWidth={2} name="Total Users" />
                  <Bar dataKey="signups" fill="#7C3AED" fillOpacity={0.4} name="New Signups" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-slate-400 text-center py-12">No growth data yet</p>
            )}
          </CardContent>
        </Card>

        {/* User Activity */}
        <Card data-testid="chart-activity">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <Activity className="w-4 h-4 text-emerald-500" />
                Daily Active Users
              </CardTitle>
              <div className="flex bg-slate-100 rounded-lg p-0.5" data-testid="activity-granularity">
                {["daily", "weekly", "monthly"].map(g => (
                  <button
                    key={g}
                    onClick={() => setActivityGranularity(g)}
                    className={`text-[10px] font-medium px-2.5 py-1 rounded-md transition-colors capitalize ${
                      activityGranularity === g ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                    data-testid={`activity-${g}`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {activity.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={activity}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip labelFormatter={(v) => `Date: ${v}`} />
                  <Line type="monotone" dataKey="unique_users" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} name="Unique Users" />
                  <Line type="monotone" dataKey="sessions" stroke="#94a3b8" strokeWidth={1} strokeDasharray="4 4" dot={false} name="Sessions" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-slate-400 text-center py-12">No activity data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Invite Funnel + Role Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Invite Timeline */}
        <Card className="lg:col-span-2" data-testid="chart-invites">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Send className="w-4 h-4 text-blue-500" />
              Invite Funnel
            </CardTitle>
          </CardHeader>
          <CardContent>
            {invites?.timeline?.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={invites.timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip labelFormatter={(v) => `Date: ${v}`} />
                  <Legend />
                  <Bar dataKey="sent" fill="#3B82F6" name="Sent" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="accepted" fill="#10B981" name="Accepted" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-slate-400 text-center py-12">No invite data yet</p>
            )}
          </CardContent>
        </Card>

        {/* Invite Status Breakdown */}
        <Card data-testid="chart-invite-status">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-amber-500" />
              Invite Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {inviteStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={inviteStatusData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {inviteStatusData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-slate-400 text-center py-12">No data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Engagement + Tool Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Insight Activity */}
        <Card className="lg:col-span-2" data-testid="chart-engagement">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-rose-500" />
              Insight Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {engagement?.insight_timeline?.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={engagement.insight_timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip labelFormatter={(v) => `Date: ${v}`} />
                  <Legend />
                  <Bar dataKey="requests" fill="#7C3AED" name="Requested" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="completed" fill="#10B981" name="Completed" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-slate-400 text-center py-12">No insight data yet</p>
            )}
          </CardContent>
        </Card>

        {/* Tool & Engagement Rates */}
        <Card data-testid="chart-tools">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Target className="w-4 h-4 text-violet-500" />
              Engagement Rates
            </CardTitle>
          </CardHeader>
          <CardContent>
            {overview && (
              <div className="space-y-5 pt-2">
                <div>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-slate-600">SWOT Completion</span>
                    <span className="font-semibold text-slate-900">{overview.swot_engagement_rate}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2.5">
                    <div className="h-2.5 rounded-full bg-violet-500 transition-all" style={{ width: `${Math.min(100, overview.swot_engagement_rate)}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-slate-600">Core Values</span>
                    <span className="font-semibold text-slate-900">{overview.values_engagement_rate}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2.5">
                    <div className="h-2.5 rounded-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, overview.values_engagement_rate)}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-slate-600">Wheel of Life</span>
                    <span className="font-semibold text-slate-900">{overview.wheel_engagement_rate}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2.5">
                    <div className="h-2.5 rounded-full bg-cyan-500 transition-all" style={{ width: `${Math.min(100, overview.wheel_engagement_rate)}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-slate-600">Insight Completion</span>
                    <span className="font-semibold text-slate-900">{overview.insight_completion_rate}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2.5">
                    <div className="h-2.5 rounded-full bg-blue-500 transition-all" style={{ width: `${Math.min(100, overview.insight_completion_rate)}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-slate-600">Invite Acceptance</span>
                    <span className="font-semibold text-slate-900">{overview.invite_acceptance_rate}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2.5">
                    <div className="h-2.5 rounded-full bg-amber-500 transition-all" style={{ width: `${Math.min(100, overview.invite_acceptance_rate)}%` }} />
                  </div>
                </div>

                {/* Tool breakdown */}
                {toolData.length > 0 && (
                  <div className="pt-3 border-t border-slate-100">
                    <p className="text-xs text-slate-400 font-medium uppercase mb-2">Insights by Tool</p>
                    {toolData.map((t) => (
                      <div key={t.name} className="flex justify-between text-sm py-1">
                        <span className="text-slate-600">{t.name}</span>
                        <span className="font-medium text-slate-900">{t.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 4: Maintenance */}
      <Card data-testid="admin-maintenance" className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <Wrench className="w-4 h-4 text-amber-500" />
            Maintenance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-slate-200 p-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
                <UserPlus className="w-4 h-4 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-slate-900 mb-0.5">
                  Backfill orphaned Circle invites
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed mb-3">
                  Finds pending Circle invites whose target email already matches a user
                  (e.g. invited from web → signed up on mobile before the auto-claim fix).
                  Idempotent — safe to re-run any time.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    onClick={() => runBackfill(true)}
                    disabled={backfillState.running}
                    variant="outline"
                    className="rounded-full text-xs h-9"
                    data-testid="admin-backfill-dry-run"
                  >
                    {backfillState.running ? "Checking…" : "Dry run"}
                  </Button>
                  <Button
                    onClick={() => setBackfillState((s) => ({ ...s, confirming: true }))}
                    disabled={backfillState.running}
                    className="rounded-full text-xs h-9 bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="admin-backfill-run"
                  >
                    Run backfill
                  </Button>
                </div>
                {backfillState.lastResult && (
                  <div
                    className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-700"
                    data-testid="admin-backfill-result"
                  >
                    <p className="font-medium text-slate-900 mb-1">
                      Last result · {backfillState.lastResult.dry_run ? "Dry run" : "Live run"} ·{" "}
                      {new Date(backfillState.lastResult.performed_at).toLocaleString()}
                    </p>
                    <ul className="space-y-0.5 tabular-nums">
                      <li>Examined: <span className="font-medium">{backfillState.lastResult.examined}</span></li>
                      <li>
                        {backfillState.lastResult.dry_run ? "Would claim" : "Claimed"}:{" "}
                        <span className="font-medium text-emerald-700">
                          {backfillState.lastResult.claimed}
                        </span>
                      </li>
                      {backfillState.lastResult.skipped_already_connected > 0 && (
                        <li>
                          Skipped (already connected):{" "}
                          <span className="font-medium">
                            {backfillState.lastResult.skipped_already_connected}
                          </span>
                        </li>
                      )}
                      {backfillState.lastResult.skipped_self_invite > 0 && (
                        <li>
                          Skipped (self-invite):{" "}
                          <span className="font-medium">
                            {backfillState.lastResult.skipped_self_invite}
                          </span>
                        </li>
                      )}
                    </ul>
                    {Array.isArray(backfillState.lastResult.rows) && backfillState.lastResult.rows.length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-[11px] text-slate-500 hover:text-slate-700">
                          Show {backfillState.lastResult.rows.length} affected{" "}
                          {backfillState.lastResult.rows.length === 1 ? "row" : "rows"}
                        </summary>
                        <ul className="mt-2 space-y-1 text-[11px] font-mono break-all max-h-48 overflow-y-auto">
                          {backfillState.lastResult.rows.map((r) => (
                            <li key={r.connection_id} className="text-slate-600">
                              {r.email} → <span className="text-violet-700">{r.claimed_user_id}</span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation dialog for live backfill run */}
      <Dialog
        open={backfillState.confirming}
        onOpenChange={(o) => setBackfillState((s) => ({ ...s, confirming: o }))}
      >
        <DialogContent data-testid="admin-backfill-confirm">
          <DialogHeader>
            <DialogTitle>Run backfill?</DialogTitle>
            <DialogDescription>
              This will claim every orphaned pending invite whose email matches an existing user
              and create the corresponding "you're connected" notifications. Idempotent — safe to
              re-run, but it can't be undone for individual rows.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setBackfillState((s) => ({ ...s, confirming: false }))}
              data-testid="admin-backfill-confirm-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => runBackfill(false)}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="admin-backfill-confirm-go"
            >
              Run now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Row 5: Users Table */}
      <Card data-testid="users-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <Users className="w-4 h-4 text-violet-500" />
            All Users ({usersList.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-3 text-slate-500 font-medium">User</th>
                  <th className="text-left py-3 px-3 text-slate-500 font-medium">Email</th>
                  <th className="text-center py-3 px-3 text-slate-500 font-medium">Source</th>
                  <th className="text-center py-3 px-3 text-slate-500 font-medium">Connections</th>
                  <th className="text-center py-3 px-3 text-slate-500 font-medium">SWOT</th>
                  <th className="text-center py-3 px-3 text-slate-500 font-medium">Values</th>
                  <th className="text-center py-3 px-3 text-slate-500 font-medium">Wheel</th>
                  <th className="text-center py-3 px-3 text-slate-500 font-medium">Requested</th>
                  <th className="text-center py-3 px-3 text-slate-500 font-medium">Given</th>
                  <th className="text-center py-3 px-3 text-slate-500 font-medium" title="Gratitude cards authored">Gratitudes</th>
                  <th className="text-left py-3 px-3 text-slate-500 font-medium">Joined</th>
                  <th className="text-right py-3 px-3 text-slate-500 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {usersList.map((u) => (
                  <tr key={u.user_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        {u.picture ? (
                          <img src={u.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center text-violet-700 text-xs font-medium">
                            {u.name?.charAt(0) || "?"}
                          </div>
                        )}
                        <div className="flex flex-col min-w-0">
                          <span className="font-medium text-slate-900 truncate">{u.name}</span>
                          {u.username ? (
                            <a
                              href={`/${u.username}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[11px] text-violet-600 hover:text-violet-700 hover:underline truncate"
                              data-testid={`user-handle-${u.user_id}`}
                              title={`@${u.username}`}
                            >
                              @{u.username}
                            </a>
                          ) : (
                            <span className="text-[11px] text-slate-400 italic">no handle yet</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-slate-600">{u.email}</td>
                    <td className="py-3 px-3 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        u.signup_source === "invited" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"
                      }`}>
                        {u.signup_source === "invited" ? "Invited" : "Organic"}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-center text-slate-700">{u.connections}</td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <div className="w-12 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full bg-green-500" style={{ width: `${u.swot_progress || 0}%` }} />
                        </div>
                        <span className="text-xs text-slate-600 font-medium w-8 text-right">{u.swot_progress || 0}%</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <div className="w-12 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full bg-violet-500" style={{ width: `${u.values_progress || 0}%` }} />
                        </div>
                        <span className="text-xs text-slate-600 font-medium w-8 text-right">{u.values_progress || 0}%</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <div className="w-12 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full bg-cyan-500" style={{ width: `${u.wheel_progress || 0}%` }} />
                        </div>
                        <span className="text-xs text-slate-600 font-medium w-8 text-right">{u.wheel_progress || 0}%</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center text-slate-700">{u.insights_requested}</td>
                    <td className="py-3 px-3 text-center text-slate-700">{u.insights_given}</td>
                    <td className="py-3 px-3 text-center" data-testid={`gratitudes-authored-${u.user_id}`}>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        (u.gratitudes_authored || 0) > 0
                          ? "bg-rose-50 text-rose-700"
                          : "bg-slate-100 text-slate-400"
                      }`}>
                        <Heart className="w-3 h-3" />
                        {u.gratitudes_authored || 0}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-slate-500 text-xs">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="py-3 px-3 text-right">
                      {u.role !== "admin" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeleteConfirm({ user_id: u.user_id, name: u.name, email: u.email })}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50"
                          data-testid={`delete-user-${u.user_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Delete User Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle>Delete user?</DialogTitle>
            <DialogDescription>
              <span className="block mt-2 text-sm text-slate-700">
                This will permanently delete <strong>{deleteConfirm?.name}</strong> ({deleteConfirm?.email}) and all their data including SWOT, Core Values, connections, and insights.
              </span>
              <span className="block mt-2 text-sm text-red-600 font-medium">
                This action cannot be undone.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteUser(deleteConfirm.user_id)}
              data-testid="confirm-delete-user"
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
